/**
 * Servicio mock para desarrollo cuando la API real no está disponible
 */
import logger from "../../utils/secureLogger";

const mockAccounts = [
  {
    id: '1',
    AccountId: '123456789012',
    AccountName: 'Production Account',
    Status: 'Active',
    Environment: 'Production',
    Region: 'us-east-1',
    CreatedDate: '2024-01-15T10:30:00Z',
    LastModified: '2024-07-10T08:15:00Z',
    Owner: 'DevOps Team',
    CostCenter: 'CC-001',
    Tags: {
      Environment: 'prod',
      Team: 'platform',
      Project: 'portal-ces'
    }
  },
  {
    id: '2',
    AccountId: '123456789013',
    AccountName: 'Development Account',
    Status: 'Active',
    Environment: 'Development',
    Region: 'us-east-1',
    CreatedDate: '2024-02-01T14:20:00Z',
    LastModified: '2024-07-09T16:45:00Z',
    Owner: 'Development Team',
    CostCenter: 'CC-002',
    Tags: {
      Environment: 'dev',
      Team: 'development',
      Project: 'portal-ces'
    }
  },
  {
    id: '3',
    AccountId: '123456789014',
    AccountName: 'Staging Account',
    Status: 'Active',
    Environment: 'Staging',
    Region: 'us-west-2',
    CreatedDate: '2024-03-10T09:15:00Z',
    LastModified: '2024-07-08T11:30:00Z',
    Owner: 'QA Team',
    CostCenter: 'CC-003',
    Tags: {
      Environment: 'staging',
      Team: 'qa',
      Project: 'portal-ces'
    }
  },
  {
    id: '4',
    AccountId: '123456789015',
    AccountName: 'Testing Account',
    Status: 'Inactive',
    Environment: 'Testing',
    Region: 'eu-west-1',
    CreatedDate: '2024-04-05T13:45:00Z',
    LastModified: '2024-06-15T10:20:00Z',
    Owner: 'Testing Team',
    CostCenter: 'CC-004',
    Tags: {
      Environment: 'test',
      Team: 'testing',
      Project: 'portal-ces'
    }
  }
];

export class MockApiService {
  constructor() {
    this.delay = 800;
  }

  async simulateDelay() {
    return new Promise(resolve => setTimeout(resolve, this.delay));
  }

  async getAccounts(params = {}) {
    await this.simulateDelay();
    
    let filteredAccounts = [...mockAccounts];
    
    if (params.status) {
      filteredAccounts = filteredAccounts.filter(account => 
        account.Status.toLowerCase() === params.status.toLowerCase()
      );
    }
    
    if (params.environment) {
      filteredAccounts = filteredAccounts.filter(account => 
        account.Environment.toLowerCase() === params.environment.toLowerCase()
      );
    }
    
    if (params.region) {
      filteredAccounts = filteredAccounts.filter(account => 
        account.Region.toLowerCase() === params.region.toLowerCase()
      );
    }
    
    if (params.search) {
      const searchTerm = params.search.toLowerCase();
      filteredAccounts = filteredAccounts.filter(account => 
        account.AccountName.toLowerCase().includes(searchTerm) ||
        account.AccountId.toLowerCase().includes(searchTerm) ||
        account.Owner.toLowerCase().includes(searchTerm)
      );
    }
    
    return {
      data: filteredAccounts,
      total: filteredAccounts.length,
      isMockData: true
    };
  }

  async createAccount(accountData) {
    await this.simulateDelay();
    
    const newAccount = {
      id: String(mockAccounts.length + 1),
      AccountId: `1234567890${String(mockAccounts.length + 12)}`,
      ...accountData,
      Status: 'Active',
      CreatedDate: new Date().toISOString(),
      LastModified: new Date().toISOString(),
      Tags: accountData.Tags || {}
    };
    
    mockAccounts.push(newAccount);
    
    return {
      data: newAccount,
      isMockData: true
    };
  }

  async updateAccount(id, accountData) {
    await this.simulateDelay();
    
    const accountIndex = mockAccounts.findIndex(account => account.AccountId === id);
    
    if (accountIndex === -1) {
      throw new Error('Account not found');
    }
    
    const updatedAccount = {
      ...mockAccounts[accountIndex],
      ...accountData,
      LastModified: new Date().toISOString()
    };
    
    mockAccounts[accountIndex] = updatedAccount;
    
    return {
      data: updatedAccount,
      isMockData: true
    };
  }

  async deleteAccount(id) {
    await this.simulateDelay();
    
    const accountIndex = mockAccounts.findIndex(account => account.AccountId === id);
    
    if (accountIndex === -1) {
      throw new Error('Account not found');
    }
    
    const deletedAccount = mockAccounts.splice(accountIndex, 1)[0];
    
    return {
      data: deletedAccount,
      isMockData: true
    };
  }

  async getAccountDetails(id) {
    await this.simulateDelay();
    
    const account = mockAccounts.find(account => account.AccountId === id);
    
    if (!account) {
      throw new Error('Account not found');
    }
    
    return {
      data: account,
      isMockData: true
    };
  }
}

export default new MockApiService();
